/* LanguageCapsule — floating pill on the landing that lets a visitor
 * switch the whole site's language in one click. Placed prominently
 * in the landing header row so it's the first thing new visitors see.
 *
 * Uses shadcn's DropdownMenu; writes the choice to localStorage via
 * i18next-browser-languagedetector's cache, and (if the user is
 * signed in) persists to `/api/auth/me/language` so it follows them
 * across devices.
 */
import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';
import { Globe, Check, ChevronDown } from 'lucide-react';
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
  DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import { SUPPORTED_LANGUAGES, LANGUAGE_STORAGE_KEY, LANGUAGE_EXPLICIT_KEY } from '@/i18n/config';
import { authApi } from '@/lib/auth_api';
import { useAuth } from '@/lib/auth_context';
import { toast } from 'sonner';

/** Landing routes that have a language-prefixed equivalent (e.g. / → /fr). */
const LANG_ROOT_PATHS = new Set(['/', '/writers', '/fr', '/de', '/es', '/pt', '/zh']);
const LANG_URL: Record<string, string> = {
  en: '/', fr: '/fr', de: '/de', es: '/es', pt: '/pt', zh: '/zh',
};

/**
 * Maps the current pathname + a new locale code to the target URL.
 * Returns null when a pure i18n switch (no navigation) is sufficient.
 *
 * Rules:
 *   • Landing roots (/, /writers, /fr, /de, …) → new locale root
 *   • Pages with locale sub-routes (/bookstore, /how-we-help-writers) → /{code}/sub-path
 *   • All other paths → null (just change i18n, no URL change needed)
 */
function resolveLocaleTarget(pathname, newCode) {
  const NON_EN = ['fr', 'de', 'es', 'pt', 'zh'];
  const parts = pathname.replace(/^\//, '').split('/').filter(Boolean);
  const hasLocalePrefix = NON_EN.includes(parts[0]);
  const canon = hasLocalePrefix ? parts.slice(1) : parts;
  const canonPath = canon.length ? '/' + canon.join('/') : '/';

  // Landing roots (/, /writers, /fr, /de, …)
  const isLandingRoot = !canon.length || (canon.length === 1 && canon[0] === 'writers');
  if (isLandingRoot || LANG_ROOT_PATHS.has(pathname)) {
    if (isLandingRoot) {
      return newCode === 'en'
        ? (canon[0] === 'writers' ? '/writers' : '/')
        : `/${newCode}`;
    }
    return LANG_URL[newCode] ?? '/';
  }

  // Pages that have dedicated locale-prefixed variants
  const LOCALE_SUBS = new Set(['bookstore', 'how-we-help-writers', 'blog']);
  if (LOCALE_SUBS.has(canon[0])) {
    return newCode === 'en' ? canonPath : `/${newCode}${canonPath}`;
  }

  return null; // No URL change – i18n update alone is enough
}


export default function LanguageCapsule({
  variant = 'landing',   // 'landing' | 'compact'
  className = '',
}) {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const navigate   = useNavigate();
  const location   = useLocation();
  const current = SUPPORTED_LANGUAGES.find((l) => l.code === (i18n.resolvedLanguage || i18n.language)?.slice(0, 2))
                || SUPPORTED_LANGUAGES[0];

  const change = useCallback(async (code) => {
    if (code === current.code) return;
    await i18n.changeLanguage(code);
    try {
      window.localStorage.setItem(LANGUAGE_STORAGE_KEY, code);
      window.localStorage.setItem(LANGUAGE_EXPLICIT_KEY, '1');
    } catch { /* ignore */ }

    // Navigate to the canonical locale URL when applicable.
    const target = resolveLocaleTarget(location.pathname, code);
    if (target !== null) {
      navigate(target, { replace: true });
    }

    if (user) {
      try {
        await authApi.setLanguage(code);
        toast.success(t('language.savedToProfile'));
      } catch (e) {
        console.warn('[i18n] failed to save language to profile', e);
      }
    }
  }, [current.code, i18n, t, user, navigate, location.pathname]);

  if (variant === 'compact') {
    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            data-testid="language-selector-compact"
            aria-label={t('language.sr_openMenu')}
            className={`inline-flex items-center gap-1.5 rounded-sm px-2 py-1 text-[11.5px] hover:bg-secondary ${className}`}
          >
            <Globe className="h-3.5 w-3.5" />
            <span>{current.nativeLabel}</span>
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="min-w-[180px] text-[12.5px]">
          <DropdownMenuLabel className="text-[10.5px] uppercase tracking-wider text-muted-foreground">
            {t('language.chooseLanguage')}
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          {SUPPORTED_LANGUAGES.map((lang) => (
            <DropdownMenuItem
              key={lang.code}
              data-testid={`language-option-${lang.code}`}
              onClick={() => change(lang.code)}
              className="flex items-center justify-between gap-2"
            >
              <span>{lang.nativeLabel}</span>
              {lang.code === current.code && <Check className="h-3.5 w-3.5" />}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    );
  }

  // 'landing' — small flag-only pill matching the top-nav button height.
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          data-testid="language-capsule"
          aria-label={t('language.sr_openMenu')}
          title={current.nativeLabel}
          className={`inline-flex h-8 shrink-0 items-center justify-center rounded-sm border p-0.5 shadow-sm transition hover:shadow-md ${className}`}
          style={{
            borderColor: 'var(--gold, #c9a24a)',
            backgroundColor: 'rgba(255,255,255,0.85)',
            backdropFilter: 'blur(4px)',
          }}
        >
          {current.flagUrl ? (
            <img
              src={current.flagUrl}
              alt={current.nativeLabel}
              className="h-6 w-6 shrink-0 rounded-[2px] object-cover"
              draggable={false}
            />
          ) : (
            <span aria-hidden="true" className="text-[16px] px-1.5" style={{ color: 'var(--oxblood, #5a1a2b)' }}>
              {current.flag || '🌐'}
            </span>
          )}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="min-w-[200px] text-[12.5px]">
        <DropdownMenuLabel className="text-[10.5px] uppercase tracking-wider text-muted-foreground">
          {t('language.chooseLanguage')}
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {SUPPORTED_LANGUAGES.map((lang) => (
          <DropdownMenuItem
            key={lang.code}
            data-testid={`language-option-${lang.code}`}
            onClick={() => change(lang.code)}
            className="flex items-center justify-between gap-2"
          >
            <span className="flex items-center gap-2">
              {lang.flagUrl ? (
                <img
                  src={lang.flagUrl}
                  alt=""
                  className="h-4 w-4 rounded-[2px] object-cover"
                  draggable={false}
                />
              ) : (
                <span className="text-[14px]" aria-hidden="true">{lang.flag}</span>
              )}
              <span className="font-medium">{lang.nativeLabel}</span>
              <span className="text-[10.5px] text-muted-foreground">{lang.label}</span>
            </span>
            {lang.code === current.code && <Check className="h-3.5 w-3.5" />}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
